import type { Category } from "@talkers/shared";
import { createSignal, type JSX, Show } from "solid-js";
import { api } from "@/api/client";
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Select,
  Stack,
  TextField,
} from "@/components/ui";
import {
  categories,
  flattenCategories,
  parseSortOrder,
  refetchCategories,
  SORT_ORDER_HINT,
} from "@/state/categories";

const ROOT = "";

export function NewCategoryDialog(props: { onClose: () => void }): JSX.Element {
  const [parentId, setParentId] = createSignal(ROOT);
  const [slug, setSlug] = createSignal("");
  const [name, setName] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [sortOrder, setSortOrder] = createSignal("100");
  const [error, setError] = createSignal<string | null>(null);
  const [saving, setSaving] = createSignal(false);

  // 3 段目は親にできない
  const parentOptions = () => [
    { value: ROOT, label: "(トップ)" },
    ...flattenCategories(categories() ?? [])
      .filter((entry) => entry.depth < 2)
      .map(({ node, depth }: { node: Category; depth: number }) => ({
        value: String(node.id),
        label: `${"　".repeat(depth)}${node.name}`,
      })),
  ];

  const canSubmit = () => slug().trim() !== "" && name().trim() !== "" && !saving();

  const submit = async () => {
    if (!canSubmit()) return;
    setSaving(true);
    setError(null);
    try {
      await api.createCategory({
        ...(parentId() === ROOT ? {} : { parentId: Number(parentId()) }),
        slug: slug().trim(),
        name: name().trim(),
        ...(description().trim() === "" ? {} : { description: description().trim() }),
        sortOrder: parseSortOrder(sortOrder()),
      });
      await refetchCategories();
      props.onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "作成できませんでした");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={props.onClose} size="sm">
      <DialogHeader>カテゴリを追加</DialogHeader>
      <DialogBody>
        <Stack gap={3}>
          <Show when={error()}>{(message) => <Alert variant="danger">{message()}</Alert>}</Show>
          <Select
            label="親カテゴリ"
            value={parentId()}
            onChange={setParentId}
            options={parentOptions()}
          />
          <TextField label="名前" value={name()} onInput={setName} required />
          <TextField
            label="slug"
            value={slug()}
            onInput={(value) => setSlug(value.toLowerCase())}
            required
            hint="URL に使う。英小文字・数字・ハイフン"
          />
          <TextField label="説明" value={description()} onInput={setDescription} />
          <TextField
            label="表示順"
            value={sortOrder()}
            onInput={setSortOrder}
            hint={SORT_ORDER_HINT}
          />
        </Stack>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={props.onClose}>
          やめる
        </Button>
        <Button variant="primary" loading={saving()} disabled={!canSubmit()} onClick={submit}>
          追加する
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
